
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Clock, Calendar } from 'lucide-react';
import { TeacherAvailabilityTemplate } from '@/hooks/useTeacherDetail';

interface TeacherAvailabilityScheduleProps {
  templates: TeacherAvailabilityTemplate[];
}

export const TeacherAvailabilitySchedule: React.FC<TeacherAvailabilityScheduleProps> = ({
  templates,
}) => {
  const getDayName = (dayOfWeek: number) => {
    const dayNames = ['อาทิตย์', 'จันทร์', 'อังคาร', 'พุธ', 'พฤหัสบดี', 'ศุกร์', 'เสาร์'];
    return dayNames[dayOfWeek] || dayOfWeek.toString();
  };

  const formatTime = (timeString: string) => {
    const time = new Date(`2000-01-01T${timeString}`);
    return time.toLocaleTimeString('th-TH', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
  };

  // Group templates by day of week
  const groupedTemplates = templates.reduce((acc, template) => {
    const day = template.day_of_week;
    if (!acc[day]) {
      acc[day] = [];
    }
    acc[day].push(template);
    return acc;
  }, {} as Record<number, TeacherAvailabilityTemplate[]>);

  if (templates.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            ตารางเวลารับงานสอน
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-center text-gray-500 py-8">ไม่มีตารางเวลาทำงาน</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Calendar className="h-5 w-5" />
          ตารางเวลารับงานสอน
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {Object.entries(groupedTemplates)
            .sort(([a], [b]) => {
              const dayA = parseInt(a);
              const dayB = parseInt(b);
              // Sort with Sunday (7) appearing after Saturday (6)
              const sortA = dayA === 7 ? 0 : dayA;
              const sortB = dayB === 7 ? 0 : dayB;
              return sortA - sortB;
            })
            .map(([dayOfWeek, dayTemplates]) => (
              <div key={dayOfWeek} className="border rounded-lg p-4">
                <h4 className="font-medium text-gray-800 mb-3 flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  {getDayName(parseInt(dayOfWeek))}
                </h4>
                
                {dayTemplates.length === 0 || !dayTemplates.some(t => t.is_working_day) ? (
                  <Badge variant="secondary" className="text-xs">
                    ไม่มีเวลาทำงาน
                  </Badge>
                ) : (
                  <div className="space-y-2">
                    {dayTemplates
                      .filter(template => template.is_working_day)
                      .sort((a, b) => a.slot_order - b.slot_order)
                      .map((template, index) => (
                        <div key={template.id} className="flex items-center gap-2">
                          <Badge variant="outline" className="text-xs flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {formatTime(template.start_time)} - {formatTime(template.end_time)}
                          </Badge>
                          <span className="text-xs text-gray-500">
                            ช่วงที่ {template.slot_order}
                          </span>
                        </div>
                      ))}
                  </div>
                )}
              </div>
            ))}
        </div>
      </CardContent>
    </Card>
  );
};
